import React, { useMemo, useCallback } from 'react'
import {
  Button,
  Select,
  Tag,
  Typography,
  Tooltip,
  message,
} from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { Input } from 'antd'
import {
  CloudOutlined,
  FilterFilled,
  CopyOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { copyToClipboard } from 'components/CopyLink/CopyText'
import styles from '../CarrierDetail.module.scss'
import type {
  RecentFailureDto,
  ProcessTimeBucket,
} from '../../../../../api/dto/get-payment-bot-dashboard.dto'
import {
  COL_WIDTHS,
  PROCESS_TIME_BUCKET_FILTERS,
  TECHNICAL_ERROR_CODE,
} from '../../../shared/constants'
import {
  formatDuration,
  processTimeColor,
  formatErrorCode,
  stripCategoryPrefix,
} from '../../../shared/utils'
import { buildPpsLogsUrl } from '../cloudwatch'
import type { CarrierRow, FailureSortColumn } from '../../../shared/types'

const { Text } = Typography

interface Args {
  isAllCarriers: boolean
  availableCarrierRows: CarrierRow[]
  selectedCarrierNames: string[]
  setSelectedCarrierNames: React.Dispatch<React.SetStateAction<string[]>>
  setFailurePage: React.Dispatch<React.SetStateAction<number>>
  tableSorter: {
    columnKey: FailureSortColumn
    order: 'ascend' | 'descend'
  } | null
  processTimeBucket: ProcessTimeBucket | null
  secondAttemptSuccess: boolean | null
  selectedBenefitId: string | null
}

// Builds the recent-failures table columns. The Carrier column (with its
// multi-select filter dropdown) is only present in the all-carriers view.
export function useCarrierFailureColumns({
  isAllCarriers,
  availableCarrierRows,
  selectedCarrierNames,
  setSelectedCarrierNames,
  setFailurePage,
  tableSorter,
  processTimeBucket,
  secondAttemptSuccess,
  selectedBenefitId,
}: Args): ColumnsType<RecentFailureDto> {
  // Multi-select dropdown options for the Carrier filter column.
  const carrierSelectOptions = useMemo(
    () =>
      [...availableCarrierRows]
        .sort((a, b) => a.carrierName.localeCompare(b.carrierName))
        .map((r) => ({ label: r.carrierName, value: r.carrierName })),
    [availableCarrierRows]
  )

  const handleCopy = useCallback((text: string) => {
    const trimmed = text?.trim()
    if (!trimmed) return

    void copyToClipboard(trimmed).then((ok) => {
      if (ok) {
        void message.success('Copied to clipboard')
      }
    })
  }, [])

  return useMemo<ColumnsType<RecentFailureDto>>(() => {
    return [
      {
        title: 'Case ID',
        dataIndex: 'paymentAssignmentId',
        key: 'paymentAssignmentId',
        width: COL_WIDTHS.taskId,
        sorter: true,
        sortOrder:
          tableSorter?.columnKey === 'paymentAssignmentId'
            ? tableSorter.order
            : null,
        render: (val: number | string | null) => {
          const fullText = val ? String(val) : '—'
          if (fullText === '—') return <Text type="secondary">—</Text>
          return (
            <Tooltip placement="topLeft" title={fullText}>
              <span
                className={styles.copyableText}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCopy(fullText)
                }}
              >
                {fullText}
                <CopyOutlined style={{ fontSize: 12 }} />
              </span>
            </Tooltip>
          )
        },
      },
      ...(isAllCarriers
        ? [
            {
              title: 'Carrier',
              dataIndex: 'carrierName',
              key: 'carrierName',
              width: COL_WIDTHS.carrierName,
              sorter: true,
              sortOrder:
                tableSorter?.columnKey === 'carrierName'
                  ? tableSorter.order
                  : null,
              filterDropdown: ({
                confirm,
                clearFilters,
              }: {
                confirm: () => void
                clearFilters?: () => void
              }) => (
                <div className={styles.filterPanel}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select carriers…"
                    value={selectedCarrierNames}
                    options={carrierSelectOptions}
                    style={{ width: '100%', marginBottom: 8 }}
                    // Render the dropdown inside the filter panel so AntD Table
                    // doesn't treat option clicks as "outside click" and close
                    // the panel before the user finishes selecting.
                    getPopupContainer={(trigger) =>
                      (trigger.closest(
                        '.ant-table-filter-dropdown'
                      ) as HTMLElement) ??
                      trigger.parentElement ??
                      document.body
                    }
                    onChange={(vals: string[]) => {
                      setSelectedCarrierNames(vals)
                      setFailurePage(1)
                    }}
                    onClear={() => {
                      setSelectedCarrierNames([])
                      setFailurePage(1)
                      clearFilters?.()
                    }}
                  />
                  <div className={styles.filterActions}>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedCarrierNames([])
                        setFailurePage(1)
                        clearFilters?.()
                        confirm()
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => confirm()}
                    >
                      OK
                    </Button>
                  </div>
                </div>
              ),
              filterIcon: (
                <FilterFilled
                  style={{
                    color:
                      selectedCarrierNames.length > 0 ? '#1890ff' : undefined,
                  }}
                />
              ),
            },
          ]
        : []),
      {
        title: 'Benefit ID',
        dataIndex: 'benefitId',
        key: 'benefitId',
        width: COL_WIDTHS.benefitId,
        sorter: true,
        sortOrder:
          tableSorter?.columnKey === 'benefitId' ? tableSorter.order : null,
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: {
          setSelectedKeys: (keys: React.Key[]) => void
          selectedKeys: React.Key[]
          confirm: () => void
          clearFilters?: () => void
        }) => (
          <div className={styles.filterPanelSm}>
            <Input
              placeholder="Search Benefit ID"
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() => confirm()}
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <div className={styles.filterActions}>
              <Button
                size="small"
                onClick={() => {
                  clearFilters?.()
                  confirm()
                }}
              >
                Reset
              </Button>
              <Button type="primary" size="small" onClick={() => confirm()}>
                Search
              </Button>
            </div>
          </div>
        ),
        filterIcon: (filtered: boolean) => (
          <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        filteredValue: selectedBenefitId ? [selectedBenefitId] : null,
        render: (val: string | null) => {
          const fullText = val ?? '—'
          if (fullText === '—') return <Text type="secondary">—</Text>
          return (
            <Tooltip placement="topLeft" title={fullText}>
              <span
                className={styles.copyableText}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCopy(fullText)
                }}
              >
                <Text ellipsis style={{ maxWidth: COL_WIDTHS.benefitId - 30 }}>
                  {fullText}
                </Text>
                <CopyOutlined style={{ fontSize: 12 }} />
              </span>
            </Tooltip>
          )
        },
      },
      {
        title: 'Error Code',
        dataIndex: 'errorCode',
        key: 'errorCode',
        width: COL_WIDTHS.errorCode,
        render: (code: string | null) =>
          code ? (
            <Tag color="error">{formatErrorCode(code)}</Tag>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Insured Name',
        dataIndex: 'insuredName',
        key: 'insuredName',
        width: COL_WIDTHS.insuredName,
        render: (name: string | null) =>
          name ? (
            <Tooltip placement="topLeft" title={name}>
              <span
                className={styles.copyableText}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCopy(name)
                }}
              >
                <Text
                  ellipsis
                  style={{ maxWidth: COL_WIDTHS.insuredName - 30 }}
                >
                  {name}
                </Text>
                <CopyOutlined style={{ fontSize: 12 }} />
              </span>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Failed At',
        dataIndex: 'failedAt',
        key: 'failedAt',
        width: COL_WIDTHS.failedAt,
        sorter: true,
        sortOrder:
          tableSorter?.columnKey === 'failedAt' ? tableSorter.order : null,
        render: (failedAt: string) =>
          dayjs(failedAt).format('YYYY-MM-DD HH:mm'),
      },
      {
        title: 'Process time',
        dataIndex: 'processTimeSec',
        key: 'processTimeSec',
        width: COL_WIDTHS.processTimeSec,
        sorter: true,
        sortOrder:
          tableSorter?.columnKey === 'processTimeSec'
            ? tableSorter.order
            : null,
        filters: PROCESS_TIME_BUCKET_FILTERS,
        filterMultiple: false,
        filteredValue: processTimeBucket ? [processTimeBucket] : null,
        render: (sec: number | null, row: RecentFailureDto) =>
          sec == null ? (
            <Text type="secondary">—</Text>
          ) : (
            <Text strong style={{ color: processTimeColor(sec, row.botType) }}>
              {formatDuration(sec)}
            </Text>
          ),
      },
      {
        title: '2nd Attempt Success',
        dataIndex: 'secondAttemptSuccess',
        key: 'secondAttemptSuccess',
        width: COL_WIDTHS.secondAttemptSuccess,
        sorter: true,
        sortOrder:
          tableSorter?.columnKey === 'secondAttemptSuccess'
            ? tableSorter.order
            : null,
        filters: [
          { text: 'Success', value: 'true' },
          { text: 'Failed', value: 'false' },
        ],
        filterMultiple: false,
        filteredValue:
          secondAttemptSuccess !== null ? [String(secondAttemptSuccess)] : null,
        render: (success: boolean) =>
          success ? (
            <Tag color="success">Success</Tag>
          ) : (
            <Tag color="error">Failed</Tag>
          ),
      },
      {
        title: 'Note',
        dataIndex: 'note',
        key: 'note',
        width: COL_WIDTHS.note,
        render: (note: string | null, row: RecentFailureDto) => {
          // For TECHNICAL_ERROR rows the generic note ("Technical error —
          // please try making manual payment") tells operators nothing; the
          // real signal is the carrier's upstream message. Surface it here
          // when present. Otherwise drop the category prefix (already shown
          // in the Error Code column) and keep the detail after the first ` — `.
          const upstream =
            row.errorCode === TECHNICAL_ERROR_CODE
              ? row.upstreamMessage?.trim()
              : undefined
          const detail = upstream || stripCategoryPrefix(note)
          return detail ? (
            <Tooltip placement="topLeft" title={detail}>
              <Text ellipsis style={{ maxWidth: COL_WIDTHS.note - 30 }}>
                {detail}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          )
        },
      },
      {
        title: 'Logs',
        key: 'logs',
        width: COL_WIDTHS.logs,
        render: (_unused: unknown, row: RecentFailureDto) => (
          <Button
            type="link"
            icon={<CloudOutlined />}
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation()
              window.open(
                buildPpsLogsUrl({
                  benefitId: row.benefitId,
                  paymentBotTaskId: row.taskId,
                  failedAtIso: row.failedAt,
                }),
                '_blank'
              )
            }}
          >
            View logs
          </Button>
        ),
      },
    ]
  }, [
    isAllCarriers,
    selectedCarrierNames,
    carrierSelectOptions,
    tableSorter,
    processTimeBucket,
    secondAttemptSuccess,
    selectedBenefitId,
    setSelectedCarrierNames,
    setFailurePage,
    handleCopy,
  ])
}
